import { ApiResponse } from '@shared/types';
import {
  FinancialPostingRuleRecord,
  SaveFinancialPostingRuleRequest,
} from '../types/accounting';
import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapFinancialPostingRule = (
  value: Record<string, unknown>,
): FinancialPostingRuleRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  source_module:
    value.source_module as FinancialPostingRuleRecord['source_module'],
  event_type: String(value.event_type || ''),
  reference_type: (value.reference_type as string | null) ?? null,
  rule_name: String(value.rule_name || ''),
  direction: value.direction as FinancialPostingRuleRecord['direction'],
  account_id: String(value.account_id || ''),
  tax_rate_id: (value.tax_rate_id as string | null) ?? null,
  payment_method_id:
    value.payment_method_id === null || value.payment_method_id === undefined
      ? null
      : toNumber(value.payment_method_id),
  priority: toNumber(value.priority),
  status: value.status as FinancialPostingRuleRecord['status'],
  metadata: (value.metadata as Record<string, unknown> | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

export const fetchFinancialPostingRules = async (
  companyId: number,
): Promise<ApiResponse<FinancialPostingRuleRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('financial_posting_rules')
      .select('*')
      .eq('company_id', companyId)
      .order('source_module', { ascending: true })
      .order('event_type', { ascending: true })
      .order('priority', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapFinancialPostingRule(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch financial posting rules',
      data: [],
    };
  }
};

export const saveFinancialPostingRule = async (
  input: SaveFinancialPostingRuleRequest,
): Promise<ApiResponse<FinancialPostingRuleRecord>> => {
  const payload = {
    company_id: input.companyId,
    outlet_id: input.outletId ?? null,
    source_module: input.sourceModule,
    event_type: input.eventType.trim(),
    reference_type: input.referenceType?.trim() || null,
    rule_name: input.ruleName.trim(),
    direction: input.direction,
    account_id: input.accountId,
    tax_rate_id: input.taxRateId ?? null,
    payment_method_id: input.paymentMethodId ?? null,
    priority: input.priority ?? 100,
    status: input.status,
    metadata: input.metadata ?? null,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('financial_posting_rules')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapFinancialPostingRule(data) };
    }

    const { data, error } = await supabase
      .from('financial_posting_rules')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapFinancialPostingRule(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save financial posting rule',
      data: undefined,
    };
  }
};

export const deleteFinancialPostingRule = async (
  companyId: number,
  ruleId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('financial_posting_rules')
      .delete()
      .eq('company_id', companyId)
      .eq('id', ruleId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete financial posting rule',
      data: null,
    };
  }
};
